import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Shield, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatEdgeFunctionError } from '@/lib/edge-function-error';

type RoleEntry = {
  id: string;
  user_id: string;
  role: string;
  email: string | null;
  full_name: string | null;
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'destructive',
  admin: 'default',
  moderator: 'secondary',
  user: 'outline',
};

export function RoleManagementCard({ currentUserId }: { currentUserId: string }) {
  const queryClient = useQueryClient();
  const [grantUserId, setGrantUserId] = useState('');
  const [grantRole, setGrantRole] = useState<string>('admin');
  const [granting, setGranting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ userId: string; role: string; email: string | null } | null>(null);
  const [revoking, setRevoking] = useState(false);

  const { data: roles, isLoading } = useQuery<RoleEntry[]>({
    queryKey: ['admin-role-list'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-manage-roles', {
        body: { action: 'list' },
      });
      if (error) throw error;
      return data?.roles || [];
    },
  });

  const handleGrant = async () => {
    if (!grantUserId.trim()) return;
    setGranting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-roles', {
        body: { action: 'grant', targetUserId: grantUserId.trim(), role: grantRole },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Granted ${grantRole} role`);
      setGrantUserId('');
      queryClient.invalidateQueries({ queryKey: ['admin-role-list'] });
    } catch (err: any) {
      toast.error(formatEdgeFunctionError(err) || 'Failed to grant role');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-roles', {
        body: { action: 'revoke', targetUserId: revokeTarget.userId, role: revokeTarget.role },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Revoked ${revokeTarget.role} role`);
      setRevokeTarget(null);
      queryClient.invalidateQueries({ queryKey: ['admin-role-list'] });
    } catch (err: any) {
      toast.error(formatEdgeFunctionError(err) || 'Failed to revoke role');
    } finally {
      setRevoking(false);
    }
  };

  // Group roles by user for display
  const grouped = (roles || []).reduce<Record<string, RoleEntry[]>>((acc, r) => {
    if (!acc[r.user_id]) acc[r.user_id] = [];
    acc[r.user_id].push(r);
    return acc;
  }, {});

  return (
    <>
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Role Management
          </CardTitle>
          <CardDescription>
            Manage admin and super-admin roles. Super-admins can toggle feature flags and security controls.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Grant role form */}
          <div className="space-y-2 rounded-md border border-border/50 bg-background/60 p-3">
            <Label className="text-xs text-muted-foreground">Grant role to user</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
                placeholder="User ID (UUID)"
                className="flex-1"
              />
              <Select value={grantRole} onValueChange={setGrantRole}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleGrant} disabled={granting || !grantUserId.trim()}>
                {granting ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
                <span className="ml-1">Grant</span>
              </Button>
            </div>
          </div>

          {/* Current roles list */}
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : Object.keys(grouped).length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No elevated roles assigned.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(grouped).map(([userId, userRoles]) => {
                const first = userRoles[0];
                const isSelf = userId === currentUserId;
                return (
                  <div
                    key={userId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/50 bg-background/60 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-foreground truncate">
                        {first.full_name || first.email || 'Unknown'}
                        {isSelf && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground truncate">{userId}</div>
                      {first.email && first.full_name && (
                        <div className="text-[11px] text-muted-foreground truncate">{first.email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {userRoles.map((r) => (
                        <div key={r.id} className="flex items-center gap-1">
                          <Badge variant={(ROLE_COLORS[r.role] as any) || 'secondary'} className="text-[10px]">
                            {r.role}
                          </Badge>
                          <button
                            className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"
                            disabled={isSelf && r.role === 'super_admin'}
                            onClick={() => setRevokeTarget({ userId, role: r.role, email: first.email })}
                            title={isSelf && r.role === 'super_admin' ? 'Cannot revoke your own super_admin role' : `Revoke ${r.role}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke role</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the <strong>{revokeTarget?.role}</strong> role from{' '}
              <strong>{revokeTarget?.email || revokeTarget?.userId}</strong>?
              {revokeTarget?.role === 'admin' && (
                <span className="block mt-1 text-destructive">
                  This will also revoke super_admin if assigned.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} disabled={revoking}>
              {revoking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
